import { useCallback, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ASSET_MERGE_UPDATED_SSE_EVENT } from '@terra/shared-contracts-applications';
import { useSSE } from '../../app/providers/SseProvider';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  mapHarEntryToHarRequest,
  type HARRequest,
} from './mapHarEntryToHarRequest';

export const TRACKED_HAR_ENTRIES_QUERY_KEY = 'har-entries';
export const TRACKED_HAR_ENTRIES_PAGE_SIZE = 50;
export const TRACKED_HAR_ENTRIES_FETCH_ERROR = 'Failed to load HAR entries';

type HarEntriesPage = {
  requests: HARRequest[];
  totalCount: number;
};

const fetchHarEntriesPage = async ({
  applicationId,
  endpointId,
  skip,
}: {
  applicationId: string;
  endpointId?: string;
  skip: number;
}): Promise<HarEntriesPage> => {
  const result = endpointId
    ? await terraProxyApiClient.getHarEntriesByEndpoint(endpointId, {
        skip,
        limit: TRACKED_HAR_ENTRIES_PAGE_SIZE,
      })
    : await terraProxyApiClient.getHarEntriesByApplication(applicationId, {
        skip,
        limit: TRACKED_HAR_ENTRIES_PAGE_SIZE,
      });

  if (!result || result.status !== 200) {
    throw new Error(TRACKED_HAR_ENTRIES_FETCH_ERROR);
  }

  return {
    requests: result.body.harEntries.map(mapHarEntryToHarRequest),
    totalCount: result.body.totalCount,
  };
};

export const useTrackedHarEntries = ({
  applicationId,
  endpointId,
}: {
  applicationId?: string;
  endpointId?: string;
}) => {
  const queryClient = useQueryClient();
  const { subscribe } = useSSE();

  useEffect(() => {
    if (!applicationId) {
      return;
    }

    return subscribe(ASSET_MERGE_UPDATED_SSE_EVENT, (event) => {
      if (event.applicationId !== applicationId) {
        return;
      }

      if (
        event.status !== 'MERGE_DONE' &&
        event.status !== 'AWAITING_FINISH' &&
        event.status !== 'REVERTED'
      ) {
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: [TRACKED_HAR_ENTRIES_QUERY_KEY, applicationId],
        exact: false,
      });
    });
  }, [applicationId, queryClient, subscribe]);

  const query = useInfiniteQuery({
    queryKey: [TRACKED_HAR_ENTRIES_QUERY_KEY, applicationId, endpointId],
    queryFn: async ({ pageParam }): Promise<HarEntriesPage> => {
      if (!applicationId) {
        return { requests: [], totalCount: 0 };
      }

      try {
        return await fetchHarEntriesPage({
          applicationId,
          endpointId,
          skip: pageParam,
        });
      } catch (error) {
        console.error('RequestsContent: Error fetching HAR data:', error);

        throw error;
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (count, page) => count + page.requests.length,
        0,
      );

      return loadedCount < lastPage.totalCount ? loadedCount : undefined;
    },
    enabled: !!applicationId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
  const requests = useMemo(
    () => query.data?.pages.flatMap((page) => page.requests) ?? null,
    [query.data?.pages],
  );
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0;
  const fetchRemainingPages = useCallback(async (): Promise<HARRequest[]> => {
    let pages = query.data?.pages ?? [];

    while (true) {
      const lastPage = pages.at(-1);
      const loadedCount = pages.reduce(
        (count, page) => count + page.requests.length,
        0,
      );

      if (
        !lastPage ||
        loadedCount >= lastPage.totalCount ||
        lastPage.requests.length === 0
      ) {
        break;
      }

      const previousLength = pages.length;
      const next = await query.fetchNextPage();
      pages = next.data?.pages ?? pages;

      if (pages.length === previousLength) {
        break;
      }
    }

    return pages.flatMap((page) => page.requests);
  }, [query.data?.pages, query.fetchNextPage]);

  return {
    requests,
    totalCount,
    isLoading: query.isPending,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    fetchRemainingPages,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
  };
};
